import { Container, Divider, Grid, Skeleton, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { SnackbarContext } from "../../../../../contexts/Snackbar";
import { useFetchClinicData } from "../../../../master/clinics/utils/DataFetching";
import { useGetTiers } from "../../../../../hooks/useGetTiers";
import SimpleAlert from "../../../../../components/display/SimpleAlert";
import { TIER_INFO } from "./enum/TierInfo";
import { TierCard } from "./components/TierCard";
import { TierDetailsDialog } from "./components/TierDetailsDialog";

export const CLINIC_TIER_FIELD = "clinic.tier";

export default function ClinicTiers() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { data: clinicData, isLoading, error } = useFetchClinicData(id);

  const { setValue, watch } = useFormContext();
  const {
    data: allTiers,
    isLoading: tiersLoading,
    error: tiersError,
  } = useGetTiers();
  const currentTierFromForm = watch(CLINIC_TIER_FIELD);
  const [selectedTierId, setSelectedTierId] = useState(clinicData?.tier?.id);
  const [openDialog, setOpenDialog] = useState(null);


  // Initialize selected tier with current tier
  useEffect(() => {
    if (clinicData?.tier?.id && !currentTierFromForm) {
      setValue(CLINIC_TIER_FIELD, clinicData.tier);
      setSelectedTierId(clinicData.tier.id);
    }
  }, [clinicData, currentTierFromForm, setValue]);

  const notCurrentTierButtonText = "ChooseTier";
  const currentTierButtonText = "CurrentTier";

  // Map the fetched tiers to the component's additional tier info
  const displayTiers =
    allTiers?.map((tier) => {
      const info = TIER_INFO[tier.tierType] || {};
      const isSelected = tier.id === selectedTierId;

      const buttonText = isSelected
        ? t(currentTierButtonText)
        : t(notCurrentTierButtonText);

      return {
        id: tier.id,
        title: tier.title,
        description: tier?.description || info.longDescription,
        longDescription: info.longDescription || tier.description,
        price: info.price || "N/A",
        features: info.features || [],
        missingFeatures: info.missingFeatures || [],
        buttonText: buttonText,
        isCurrent: isSelected,
        tierType: tier.tierType,
      };
    }) || [];

  const handleTierSelect = (tier) => {
    setSelectedTierId(tier.id);
    setValue(CLINIC_TIER_FIELD, tier);
  };
  const handleDetailsClick = (tierId) => {
    setOpenDialog(tierId);
  };

  const handleCloseDialog = () => {
    setOpenDialog(null);
  };

  if (isLoading || tiersLoading) {
    return <Skeleton variant="rectangular" height={118} />;
  }

  if (error || tiersError) {
    return <SimpleAlert message={"ErrorFetchingTiers"} severity={"error"} />;
  }

  // Find the specific tier data for the dialog before rendering
  const selectedTierForDialog = displayTiers.find(
    (tier) => tier.id === openDialog
  );

  return (
    <Fragment>
      <Container sx={{ mt: 5 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t("Tiers")}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid
          container
          spacing={4}
          justifyContent="center"
          alignItems="stretch"
          mt={4}
        >
          {displayTiers.map((tier) => (
            <Grid
              item
              key={tier.id}
              xs={12}
              sm={6}
              md={6}
              sx={{ display: "flex" }}
            >
              <TierCard
                tier={tier}
                onTierSelect={handleTierSelect}
                onDetailsClick={handleDetailsClick}
                testId={`clinic.tiers.card.${tier.tierType}`}
              />
            </Grid>
          ))}
        </Grid>
      </Container>

      <TierDetailsDialog
        tier={selectedTierForDialog}
        isOpen={Boolean(openDialog)}
        onClose={handleCloseDialog}
      />
    </Fragment>
  );
}
